import type { ReactNode } from 'react'
import type { NextMeeting } from '../api/client'
import { Mark } from './Mark'
import { Assistant } from './Assistant'

export type Screen =
  | 'home' | 'table' | 'meetings' | 'commitments'
  | 'cabinet' | 'numbers' | 'board' | 'company' | 'settings'
  | 'hub'

/*
  The five destinations under 900px, and the screens the fifth one owns.

  'hub' is a screen in its own right, not a menu: it is the Company tab, and it lists the
  things that are facts about the business rather than things you do. That distinction is
  what makes five tabs enough where eight never were. The two previous attempts at a bottom
  bar both failed by hiding half the destinations behind an overflow sheet, which is a
  different thing entirely and deserved to fail.

  A hub child keeps the Company tab lit. Walking from the hub into The Numbers and finding
  no tab active is how a bar like this stops telling you where you are.
*/
const HUB_CHILDREN = new Set<Screen>(
  ['hub', 'board', 'company', 'cabinet', 'numbers', 'settings'])

/*
  What the top bar calls each screen.

  Home is absent on purpose: it shows the lockup instead, which is the one place the brand
  still appears in the mobile chrome now that the bar is no longer a navy slab. Everywhere
  else the bar says where you are, because a screen that does not name itself makes the
  owner reconstruct it from the content.

  These are the sidebar's labels with two exceptions. 'company' is "Company profile" here
  rather than "Company", because the Company tab is the hub and a child that shares its
  parent's name is how you lose track of which one you are on. 'board' gains its article to
  match The Table and The Numbers.
*/
const TITLE: Partial<Record<Screen, string>> = {
  table:       'The Table',
  meetings:    'Meetings',
  commitments: 'Actions',
  cabinet:     'File Cabinet',
  numbers:     'The Numbers',
  board:       'The Board',
  company:     'Company profile',
  settings:    'Settings',
  hub:         'Company',
}

/*
  UI SPEC §1.

  Capture is deliberately not in the nav. Nav is for places; capture is an action, and it
  stays reachable from every screen including mid-meeting.

  The table's badge lives in the shell because §1 requires the count visible at all times
  and the table one tap from everywhere.
*/

const ITEMS: { id: Screen; label: string; icon: ReactNode }[] = [
  { id: 'home',        label: 'Home',    icon: <IconHome /> },
  { id: 'table',       label: 'The Table', icon: <IconInbox /> },
  { id: 'meetings',    label: 'Meetings', icon: <IconMeetings /> },
  { id: 'commitments', label: 'Actions', icon: <IconCommitments /> },
  { id: 'cabinet',     label: 'File Cabinet', icon: <IconCabinet /> },
  { id: 'numbers',     label: 'The Numbers', icon: <IconNumbers /> },
  { id: 'board',       label: 'Board',   icon: <IconBoard /> },
  { id: 'company',     label: 'Company', icon: <IconCompany /> },
  { id: 'settings',    label: 'Settings', icon: <IconSettings /> },
]

/*
  Screens that hold a list rather than a form.

  The 720px reading cap is right for Company and Settings, which are single columns of
  fields, and wrong for these: measured at a 1440 viewport they laid out at 672px inside
  1208px of room, so most of the tabs carried 536px of empty slate down the right and the
  page width changed under you on every navigation.

  The Table held the cap for a while, on the argument that its rows are the owner's own
  sentences and a sentence set 1100px wide is harder to read. Half of that was right. A
  closed row is a six-word label and a one-line preview clamped with an ellipsis, so the
  width buys more of the sentence rather than a longer line, and the measure never came
  into it. What the cap actually bought was the lurch: The Table is the tab an owner is on
  most, and it was the only list screen that snapped 528px narrower when you reached it.

  The prose the old note was protecting is real, but it is inside an opened item, so that
  is where the measure belongs. `.msg` carries it. Same move as the File Cabinet and The
  Numbers: the screen takes the width, the parts holding sentences keep a measure.

  Company and Settings went the same way and were the last two holding the cap. They are
  single columns of fields, and the note was right that a row spread across 1200px is
  worse than a narrow one: a label on the left and its chevron a metre to the right is not
  a control anybody aims at. So neither screen spreads its rows. Their cards sit two
  abreast above 1080px instead, which uses the page and keeps every row short. `.colstack`
  in app.css does it.

  What is left capped is capped on purpose, and both are prose: the minutes at 860 and the
  live meeting at 72ch. A measure is the right cap for a page of sentences. This set is
  for screens whose content is rows, tiles or a list.
*/
const WIDE = new Set<Screen>([
  'table', 'numbers', 'cabinet', 'commitments', 'meetings', 'board', 'company', 'settings',
])

export function Shell({
  screen, onNavigate, onCapture, onSignOut, next, inboxCount, company,
  title, onBack, children,
}: {
  screen: Screen
  onNavigate: (s: Screen) => void
  onCapture: () => void
  onSignOut: () => void
  next: NextMeeting | null
  inboxCount: number
  /** The business name. Only the first letter is used, on the Company tab's disc. */
  company: string | null
  /*
    What the top bar should say, when the screen alone does not settle it. Meetings is the
    only one so far: the same screen is a ledger, a set of minutes or a room with a meeting
    running in it, and the bar has to say which.
  */
  title?: string | null
  /*
    Where back goes, when a screen has been drilled into. Absent, a hub child still gets a
    back to the hub, because that is the path the owner took to reach it.
  */
  onBack?: (() => void) | null
  children: ReactNode
}) {
  const overdue = next?.overdueCommitmentCount ?? 0

  return (
    // The shell is always on the canvas; only Home's content area joins it. Every other
    // screen keeps paper under it, and the meeting room in particular has to stay light
    // for dissent red and moved amber to read.
    <div className="shell on-canvas">
      <nav className="nav" aria-label="Main">
        {/* The logo, reversed for the navy: gold sail unchanged, wedge and letters in
            white. The wordmark keeps its gold dot, which is why it is a recoloured asset
            rather than a filtered one. */}
        <div className="nav__brand">
          <Mark variant="on-navy" className="nav__mark" />
          <img className="nav__word" src="/brand/wordmark-white.png" alt="Boardium" />
        </div>

        {ITEMS.map(item => {
          const badge =
            item.id === 'table' && inboxCount > 0 ? { n: inboxCount, alert: false } :
            item.id === 'commitments' && overdue > 0 ? { n: overdue, alert: true } :
            null

          return (
            <a
              key={item.id}
              href={`#${item.id}`}
              className={`nav__item${screen === item.id ? ' is-active' : ''}`}
              aria-current={screen === item.id ? 'page' : undefined}
              onClick={e => { e.preventDefault(); onNavigate(item.id) }}
            >
              <span className="nav__icon" aria-hidden>{item.icon}</span>
              <span>{item.label}</span>
              {badge && (
                <span className={`nav__badge${badge.alert ? ' nav__badge--alert' : ''}`}>
                  {badge.n}
                </span>
              )}
            </a>
          )
        })}

        {/* UI SPEC §3.2's chip, in the place it belongs on desktop. The sidebar had
            nine hundred pixels of nothing under Company. */}
        {next?.scheduledFor && (
          <div className="nav__chip">
            <span className="nav__chip-label">Next meeting</span>
            <span className="nav__chip-when">
              {new Date(next.scheduledFor).toLocaleDateString(undefined, {
                weekday: 'long', day: 'numeric', month: 'short',
              })}
            </span>
            {/* Five slots, always: see the note on the Next meeting card. */}
            <div className="dots" aria-hidden>
              {Array.from({ length: 4 }, (_, i) => (
                <span key={i} className={`dot ${i < Math.min(next.itemCount, 4) ? 'is-filled' : ''}`} />
              ))}
            </div>
            <span className="nav__chip-line">
              {next.itemCount === 0
                ? 'Nothing on the table yet'
                : `${next.itemCount} ${next.itemCount === 1 ? 'item' : 'items'} waiting`}
            </span>
          </div>
        )}

        <button className="nav__signout" onClick={onSignOut}>Sign out</button>
      </nav>

      <MobileChrome
        screen={screen}
        onNavigate={onNavigate}
        onCapture={onCapture}
        next={next}
        inboxCount={inboxCount}
        overdue={overdue}
        company={company}
        title={title}
        onBack={onBack}
      />

      {/*
        The profile nudge used to live here, above every screen, which made "Add your annual
        revenue" the first line on all nine tabs and pushed each screen's own title below a
        request. It is on Home now, under the masthead: still unavoidable, since Home is
        where every session starts, and no longer the thing the product says instead of
        hello.
      */}
      <main className={`main${screen === 'home' ? ' main--canvas on-canvas' : ''}${WIDE.has(screen) ? ' main--wide' : ''}`}>
        {children}
      </main>

      {/*
        The floating capture button used to sit here and is gone.

        It was the only capture affordance on a desktop screen that is not Home, so this
        is a real loss and not a tidy-up: from The Table or Meetings on a wide screen
        there is now no way to capture without going back to Home's Ask card. It came out
        because it was reported as doing nothing, and because the corner it held is where
        a help launcher belongs. If capture needs a home again it should be a control in
        the page rather than a second thing floating over it.
      */}

      {/* Desktop only: on a phone this is a hub row, because the foot of the screen
          belongs to the tab bar. See .asst__launch in app.css. */}
      <Assistant screen={title ?? TITLE[screen] ?? 'Home'} />
    </div>
  )
}

/* Icons are inline and stroke-based so they inherit currentColor and stay hairline. */

function stroke() {
  return {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  }
}

function IconHome() {
  return <svg viewBox="0 0 24 24" width="100%" height="100%" {...stroke()}>
    <path d="M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-4v-5H9v5H5a1 1 0 0 1-1-1z" />
  </svg>
}

function IconInbox() {
  return <svg viewBox="0 0 24 24" width="100%" height="100%" {...stroke()}>
    <path d="M4 13h4l1.5 3h5L16 13h4M4 13V6a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v7m-16 0v5a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-5" />
  </svg>
}

function IconMeetings() {
  return <svg viewBox="0 0 24 24" width="100%" height="100%" {...stroke()}>
    <rect x="4" y="6" width="16" height="14" rx="1.5" /><path d="M4 10h16M9 4v3M15 4v3" />
  </svg>
}

function IconCommitments() {
  return <svg viewBox="0 0 24 24" width="100%" height="100%" {...stroke()}>
    <path d="m5 12.5 4 4 10-10" />
  </svg>
}

function IconBoard() {
  return <svg viewBox="0 0 24 24" width="100%" height="100%" {...stroke()}>
    <circle cx="8" cy="9" r="2.5" /><circle cx="16" cy="9" r="2.5" />
    <path d="M3.5 19c0-2.5 2-4 4.5-4s4.5 1.5 4.5 4M12.5 19c0-2.5 1.5-4 3.5-4s4.5 1.5 4.5 4" />
  </svg>
}

function IconCabinet() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="4" y="3" width="16" height="8" rx="1.5" />
      <rect x="4" y="13" width="16" height="8" rx="1.5" />
      <path d="M10 7h4M10 17h4" />
    </svg>
  )
}

function IconNumbers() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </svg>
  )
}

function IconCompany() {
  return <svg viewBox="0 0 24 24" width="100%" height="100%" {...stroke()}>
    <path d="M4 20V7l7-3v16M11 20h9V11l-9-3M7 11h1M7 15h1M15 13h1M15 17h1" />
  </svg>
}

function IconSettings() {
  return <svg viewBox="0 0 24 24" width="100%" height="100%" {...stroke()}>
    <circle cx="12" cy="12" r="3" />
    <path d="M12 2v3M12 19v3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M2 12h3M19 12h3M4.9 19.1L7 17M17 7l2.1-2.1" />
  </svg>
}

function IconPlus() {
  return <svg viewBox="0 0 24 24" width="24" height="24" {...stroke()} strokeWidth={1.75}>
    <path d="M12 5v14M5 12h14" />
  </svg>
}

/* ── Mobile: a fixed top bar and a bottom tab bar ───────────────────────── */

/*
  Five tabs, and the fifth is a place.

  Eight labelled destinations do not fit across the foot of a phone. Both previous attempts
  proved it, and both got there the same way: they kept the eight and hid four of them
  behind an overflow sheet, which is a menu wearing a tab's clothes. The fix is not a
  smaller font. It is noticing that four of those eight are not destinations you visit in
  the course of doing something, they are reference: the board, the profile, the files, the
  figures. Those belong to the business, so they live behind the business.

  Home, The Table, Meetings and Actions are what an owner does. Company is what the owner
  has. That is the whole of the information architecture, and it is why five is enough.

  Capture is still not a destination, so it is still not in the bar. It moved to the top
  bar's right slot, which had been reserved and empty since the drawer was built. The rule
  in this file's header holds: capture stays reachable from every screen including
  mid-meeting.
*/

const TABS: { id: Screen; label: string; icon: ReactNode }[] = [
  { id: 'home',        label: 'Home',     icon: <IconHome /> },
  { id: 'table',       label: 'Table',    icon: <IconInbox /> },
  { id: 'meetings',    label: 'Meetings', icon: <IconMeetings /> },
  { id: 'commitments', label: 'Actions',  icon: <IconCommitments /> },
]

function MobileChrome({
  screen, onNavigate, onCapture, next, inboxCount, overdue, company, title, onBack,
}: {
  screen: Screen
  onNavigate: (s: Screen) => void
  onCapture: () => void
  next: NextMeeting | null
  inboxCount: number
  overdue: number
  company: string | null
  title?: string | null
  onBack?: (() => void) | null
}) {
  const hubActive = HUB_CHILDREN.has(screen)
  const heading = title ?? TITLE[screen] ?? null

  /*
    Back is the path the owner actually took, not a guess at a hierarchy. A screen that
    said where it goes says so; otherwise a hub child returns to the hub, because on a
    phone the hub is the only way it could have been reached. Home and the four other tabs
    are roots and have no back at all: the tab bar is how you leave them.
  */
  const back =
    onBack ? onBack :
    (HUB_CHILDREN.has(screen) && screen !== 'hub') ? () => onNavigate('hub') :
    null

  return (
    <>
      {/*
        The bar is the page colour now rather than a navy slab, and content scrolls under
        it behind a blur. Two reasons, and the second is the one that decided it.

        It matches the reference. And a full-width navy band that cannot be tapped
        contradicts the rule the whole repaint rests on, which is that navy means you can
        act on this. The brand survives as the lockup on Home, which is where a masthead
        belongs anyway.
      */}
      <header className="mtop">
        {back ? (
          <button className="mtop__back" onClick={back} aria-label="Back">
            <IconChevronLeft />
          </button>
        ) : (
          <span className="mtop__spare" aria-hidden />
        )}

        {screen === 'home' ? (
          /* The lockup, on light. Tapping it goes Home, which is where it already is;
             harmless, and the alternative is a logo that looks disabled. */
          <button className="mtop__brand" onClick={() => onNavigate('home')} aria-label="Boardium, home">
            <Mark variant="on-light" className="mtop__mark" live={next?.chipState === 'running'} />
            <img className="mtop__word" src="/brand/wordmark-ink.png" alt="Boardium" />
          </button>
        ) : (
          <h1 className="mtop__title">{heading}</h1>
        )}

        <button className="mtop__capture" onClick={onCapture} aria-label="Capture something">
          <IconPlus />
        </button>
      </header>

      <nav className="tabbar" aria-label="Main">
        {TABS.map(item => {
          const badge =
            item.id === 'table' && inboxCount > 0 ? { n: inboxCount, alert: false } :
            item.id === 'commitments' && overdue > 0 ? { n: overdue, alert: true } :
            null

          return (
            <button
              key={item.id}
              className={`tab${screen === item.id ? ' is-active' : ''}`}
              aria-current={screen === item.id ? 'page' : undefined}
              onClick={() => onNavigate(item.id)}
            >
              <span className="tab__icon" aria-hidden>
                {item.icon}
                {badge && (
                  <span className={`tab__badge${badge.alert ? ' tab__badge--alert' : ''}`}>
                    {badge.n}
                  </span>
                )}
              </span>
              <span className="tab__label">{item.label}</span>
            </button>
          )
        })}

        {/*
          The disc carries the company's initial rather than a building glyph, because this
          tab is one particular business and a generic icon would say only "settings".
        */}
        <button
          className={`tab${hubActive ? ' is-active' : ''}`}
          aria-current={hubActive ? 'page' : undefined}
          onClick={() => onNavigate('hub')}
        >
          <span className="tab__icon" aria-hidden>
            <span className="tab__disc">{initialOf(company)}</span>
          </span>
          <span className="tab__label">Company</span>
        </button>
      </nav>
    </>
  )
}

/** First letter of the business name, or a fallback that is not an empty circle. */
function initialOf(company: string | null): string {
  const first = (company ?? '').trim()[0]
  return first ? first.toUpperCase() : 'B'
}

function IconChevronLeft() {
  return <svg viewBox="0 0 24 24" width="22" height="22" {...stroke()} strokeWidth={1.75}>
    <path d="m15 5-7 7 7 7" />
  </svg>
}
